import { useState, useRef, useEffect } from "react";
import { Button } from "@component/button";
import {
  PencilIcon,
  SaveIcon,
  BriefcaseIcon,
  AlertCircleIcon,
  XIcon,
  SparklesIcon,
} from "lucide-react";
import { PersonalInfo } from "@container/personal-info";
import { ProfessionalSummary } from "@container/professional-summary";
import { WorkExperience } from "@container/work-experience";
import { Skills } from "@container/skills";
import { Certifications } from "@container/certifications";
import { Education } from "@container/education";
import { ResumeDocument } from "@container/resume-document";
import { Awards } from "@container/awards";
import { Projects } from "@container/projects";
import { SupportingDocuments } from "@container/supporting-documents";
import { getCVAnalyze, getMyCV, updateCV } from "@service/cv";
import {
  CVDataInfo,
  Education as EducationType,
  GetMyCVResponse,
  Award as AwardType,
  WorkExperience as WorkExperienceType,
  Certificate,
  Publication,
  Project,
  CandidateCVAnalyzedResponse,
} from "@type/cv";
import { WorkPreferencesModal } from "@container/work-preferences-modal";
import { catchErrorLog } from "@util/sentry";
import { getCookie, setCookie } from "@util/cookie";
import NotificationDot from "@component/notification-dot";
import LoadingBox from "@component/loading-box";
import AuthLayout from "@container/layout/auth";
import ResumeAnalyzeTooltip from "@container/resume-analyze-tooltip";
import LoadingOverlay from "@component/loading-overlay";

export default function ProfileReview() {
  const [isEditing, setIsEditing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isAnalized, setIsAnalyzed] = useState<boolean>(false);
  const [cvData, setCvData] = useState<GetMyCVResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUpdateCV, setLoadingUpdateCV] = useState<boolean>(false);
  const [isCVUpdated, setIsCVUpdated] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isWorkPreferencesModalOpen, setIsWorkPreferencesModalOpen] =
    useState(false);
  const [cvDataInfo, setCVDataInfo] = useState<CVDataInfo | undefined>(
    undefined
  );
  const [cvAnalyzeData, setCVAnalyzeData] = useState<CandidateCVAnalyzedResponse | null>(null);
  const [isShowBannerCompleteProfile, setShowBannerCompleteProfile] = useState<boolean>(!getCookie('__banner_complete_profile__'))
  const [isShowDotWorkPreference, setShowDotWorkPreference] = useState<boolean>(!getCookie('__dot_work_preference__'));

  // Create refs for each section
  const profileRef = useRef<HTMLElement>(null);
  const experienceRef = useRef<HTMLElement>(null);
  const educationRef = useRef<HTMLElement>(null);
  const skillsRef = useRef<HTMLElement>(null);
  const certificationsRef = useRef<HTMLElement>(null);
  const awardsRef = useRef<HTMLElement>(null);
  const projectsRef = useRef<HTMLElement>(null);


  const fetchCVData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getMyCV();
      setCvData(response);
      if (response.code === 200 && response?.data) {
        setCVDataInfo({
          CV_data: response.data.CV_data,
          summary_data: response.data.summary_data,
          profile_picture_path: response.data.profile_picture_path || '',
          pdf_path: response.data.pdf_path || '',
          uploaded_at: response.data.uploaded_at || '',
          filename: response.data.filename || ''
        });
      } else {
        setCVDataInfo({
          CV_data: {
            name: "",
            country_code: "",
            phone: "",
            email: "",
            avatar: "",
            awards: [],
            certifications: [],
            education: [],
            location: "",
            position: "",
            professional_summary: "",
            projects: [],
            publications: [],
            skills: [],
            work_experience: []
          },
          summary_data: {
            highest_education: "",
            relevant_tags: [],
            years_of_experience: 0,
          },
          profile_picture_path: '',
          pdf_path: '',
          uploaded_at: '',
          filename: ''
        });
      }
    } catch (err) {
      setError("Failed to fetch CV data.");
      catchErrorLog(`failed to fetch CV data, err: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCVData();
  }, []);

  const onChangeCVDataInfo = (
    field: string,
    values:
      | string
      | WorkExperienceType[]
      | EducationType[]
      | string[]
      | Certificate[]
      | AwardType[]
      | Publication[]
      | Project[]
  ) => {
    // change
    if (cvDataInfo) {
      setCVDataInfo({
        ...cvDataInfo,
        CV_data: {
          ...cvDataInfo.CV_data,
          [field]: values,
        },
      });
    }
  };

  const onChangeBaseInfo = (values: { name: string; email: string; phone: string; location: string; position: string; country_code: string }) => {
    setCVDataInfo({
      ...cvDataInfo,
      // @ts-ignore
      CV_data: {
        ...cvDataInfo?.CV_data,
        name: values.name,
        email: values.email,
        phone: values.phone,
        location: values.location,
        position: values.position,
        country_code: values.country_code,
      },
    });
  };

  const handleEditToggle = () => {
    if (isEditing && cvData?.data) {
      setCVDataInfo({
        CV_data: cvData.data.CV_data,
        summary_data: cvData.data.summary_data,
        pdf_path: cvData.data.pdf_path,
        filename: cvData.data.filename,
        uploaded_at: cvData.data.uploaded_at,
        profile_picture_path: cvData.data.profile_picture_path,
      });
    }

    fetchCVData();
    setIsEditing(!isEditing);
  };

  const onSaveCVData = async () => {
    if (!cvDataInfo) return;

    setLoadingUpdateCV(true);
    setIsCVUpdated(false);
    try {
      const workExperience = cvDataInfo?.CV_data.work_experience.sort((a, b) => {
        if (a.is_current) return -1;
        if (b.is_current) return 1;
        const yearDiff = b.period_end_year - a.period_end_year;
        if (yearDiff !== 0) return yearDiff;
        return b.period_end_month - a.period_end_month;
      });

      await updateCV({
        CV_data: {
          ...cvDataInfo?.CV_data,
          work_experience: workExperience,
        },
        summary_data: cvDataInfo?.summary_data,
        profile_picture_path: cvDataInfo?.profile_picture_path || "",
      });

      setIsCVUpdated(true);
    } catch (err) {
      setError("Failed to fetch CV data.");
      console.error("Error fetching CV data:", err);
    } finally {
      setIsEditing(false);
      setLoadingUpdateCV(false);
    }
  };

  const onHideLoadingOverlay = () => {
    fetchCVData();
    setIsCVUpdated(false);
  }

  const handleWorkPreferencesClick = () => {
    setIsWorkPreferencesModalOpen(true);
    setShowDotWorkPreference(false);
    setCookie('__dot_work_preference__', 'true');
  };

  const handleWorkPreferencesClose = () => {
    setIsWorkPreferencesModalOpen(false);
  };

  const onAnalyze = async () => {
    setIsAnalyzing(true);
    getCVAnalyze().then((resp) => {
      if (resp.status === 200) {
        setIsAnalyzed(true);
        setCVAnalyzeData(resp.data.data);
      }
    }).finally(() => {
      setIsAnalyzing(false);
    });
  }

  const onCancelAnalyze = () => {
    setIsAnalyzed(false);
  }

  let isDisabledButton = false;
  if (cvDataInfo) {
    isDisabledButton =
      !cvDataInfo.CV_data.name ||
      !cvDataInfo.CV_data.email;


    if (cvDataInfo.CV_data.work_experience.length > 0) {
      for (let i = 0; i < cvDataInfo.CV_data.work_experience.length; i++) {
        const experience = cvDataInfo.CV_data.work_experience[i];
        if (!experience.role || !experience.institution || !experience.period_start_year || (!experience.is_current && experience.period_end_year === 0) || (experience.period_end_year < experience.period_start_year)) {
          isDisabledButton = true;
          break;
        }
      }
    }

    if (cvDataInfo.CV_data.education.length > 0) {
      for (let i = 0; i < cvDataInfo.CV_data.education.length; i++) {
        const education = cvDataInfo.CV_data.education[i];
        if (!education.degree || !education.institution || !education.period) {
          isDisabledButton = true;
          break;
        }
      }
    }

    if (cvDataInfo.CV_data.certifications.length > 0) {
      for (let i = 0; i < cvDataInfo.CV_data.certifications.length; i++) {
        const certification = cvDataInfo.CV_data.certifications[i];
        if (!certification.name || !certification.institution || !certification.date) {
          isDisabledButton = true;
          break;
        }
      }
    }

    if (cvDataInfo.CV_data.awards.length > 0) {
      for (let i = 0; i < cvDataInfo.CV_data.awards.length; i++) {
        const award = cvDataInfo.CV_data.awards[i];
        if (!award.name || !award.institution || !award.date) {
          isDisabledButton = true;
          break;
        }
      }
    }

    if (cvDataInfo.CV_data.projects.length > 0) {
      for (let i = 0; i < cvDataInfo.CV_data.projects.length; i++) {
        const project = cvDataInfo.CV_data.projects[i];
        if (!project.name || !project.role || !project.year) {
          isDisabledButton = true;
          break;
        }
      }
    }
  }


  return (
    <AuthLayout withoutHeader>
      <div className="px-0 py-0">
        {/* Loading State */}
        {isLoading && (
          <LoadingBox />
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-8">
            {error}
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && cvData && (
          <>
            {/* Review Header */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-3 sm:mb-6 space-y-3 sm:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-black">
                  My Resume
                </h1>
                <p className="text-gray-600">
                  This information will be shown to companies to help you get hired.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div>
                  {isAnalized ? (
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={onCancelAnalyze}
                    >
                      <XIcon className="h-4 w-4" />

                      <span>Cancel Analysis</span>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white relative"
                      onClick={onAnalyze}
                      disabled={isAnalyzing}
                    >
                      <SparklesIcon
                        className="h-4 w-4 fill-blue-600"
                      />


                      <span>{isAnalyzing ? "Analyzing Resume..." : "Analyze Resume"}</span>
                      {isAnalyzing && (
                        <span className="ml-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      )}
                    </Button>
                  )}
                </div>
                <div className="flex flex-row gap-2">
                  {import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW !== "true" && (
                    <Button
                      onClick={handleWorkPreferencesClick}
                      variant="outline"
                      className="relative"
                    >
                      {isShowDotWorkPreference && (
                        <div className="absolute -top-[8px] -right-1">
                          <NotificationDot size="small" />
                        </div>
                      )}
                      <BriefcaseIcon className="h-4 w-4" />
                      Work Preferences
                    </Button>
                  )}
                  {isEditing ? (
                    <Button
                      variant="default"
                      disabled={isLoadingUpdateCV}
                      onClick={onSaveCVData}
                    >
                      <SaveIcon className="h-4 w-4" />
                      Save
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={cvDataInfo?.filename === ""}
                      onClick={() => setIsEditing(true)}
                    >
                      <PencilIcon className="h-4 w-4" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {isShowBannerCompleteProfile && (
              <div
                className="bg-blue-50 border border-blue-100 rounded-lg p-6 mb-6"
                style={{ paddingTop: "18px", paddingBottom: "18px" }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-2.5 rounded-full hidden sm:block">
                      <AlertCircleIcon className="h-5 w-5 text-blue-600" />
                    </div>

                    <div className="flex-1">
                      <h3
                        className="text-base font-medium text-gray-800"
                        style={{
                          borderRadius: "0px",
                          paddingRight: "10px",
                          paddingLeft: "0px",
                        }}
                      >
                        Complete your profile - including your profile photo, skills,
                        experience, and location - to boost your chances of getting hired.
                      </h3>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-gray-400 hover:text-gray-600 rounded-full self-center"
                    onClick={() => {
                      setCookie('__banner_complete_profile__', 'true');
                      setShowBannerCompleteProfile(false);
                    }}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {!isLoading && (
              <ResumeDocument
                path={cvDataInfo?.filename || ""}
                uploaded_at={cvDataInfo?.uploaded_at || ""}
                onCallback={fetchCVData}
              />
            )}

            {/* Supporting Documents — sits directly under the CV upload
                card so the candidate manages all uploaded files (CV +
                supplementary PDFs) in one stack. Demo-practice only. */}
            {import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true" && (
              <div className="mt-6">
                <SupportingDocuments isEditing={isEditing} />
              </div>
            )}

            {/* All sections displayed vertically */}
            <div className="space-y-10 mt-6">
              {/* Profile Section */}
              <section ref={profileRef}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1">
                    <div className="bg-white dark:bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                      <div className="flex items-center mb-4 space-x-2">
                        <h2 className="text-xl font-semibold text-black">
                          Personal Information
                        </h2>
                        {isAnalized && (
                          <ResumeAnalyzeTooltip
                            content={cvAnalyzeData?.personal_info.analyze_message || ""}
                            type={cvAnalyzeData?.personal_info.status || ""}
                            onClose={() => { }}
                          />
                        )}
                      </div>
                      <PersonalInfo
                        isAnalized={isAnalized}
                        isEditing={isEditing}
                        data={cvDataInfo?.CV_data || undefined}
                        profile_picture_path={cvDataInfo?.profile_picture_path || ''}
                        onChange={(newValue) => {
                          onChangeBaseInfo(newValue);
                        }}
                      />
                    </div>

                  </div>
                  <div className="md:col-span-2">
                    <div className="bg-white dark:bg-white rounded-lg p-6 shadow-sm border border-gray-200 h-full">
                      <div className="flex items-center mb-4 space-x-2">
                        <h2 className="text-xl font-semibold text-black">
                          Professional Summary
                        </h2>
                        {isAnalized && (
                          <ResumeAnalyzeTooltip
                            content={cvAnalyzeData?.professional_summary.analyze_message || ""}
                            type={cvAnalyzeData?.professional_summary.status || ""}
                            onClose={() => { }}
                          />
                        )}
                      </div>
                      <ProfessionalSummary
                        isEditing={isEditing}
                        data={cvDataInfo?.CV_data.professional_summary || ""}
                        onChange={(newValue: string) => {
                          onChangeCVDataInfo("professional_summary", newValue);
                        }}
                      />
                    </div>

                  </div>
                </div>
              </section>

              {/* Skills Section */}
              <section ref={skillsRef}>
                <div className="flex items-center mb-2 space-x-2">
                  <h2 className="text-xl font-bold text-black flex items-center gap-2">
                    Skills
                  </h2>
                  {isAnalized && (
                    <ResumeAnalyzeTooltip
                      content={cvAnalyzeData?.skills.analyze_message || ""}
                      type={cvAnalyzeData?.skills.status || ""}
                      position="right"
                      onClose={() => { }}
                    />
                  )}
                </div>
                <Skills
                  isEditing={isEditing}
                  isDisabled={cvDataInfo?.filename === ""}
                  data={cvDataInfo?.CV_data.skills || []}
                  onChange={(newValues: string[]) => {
                    onChangeCVDataInfo("skills", newValues);
                  }}
                />
              </section>

              {/* Experience Section */}
              <section ref={experienceRef}>
                <div className="flex items-center mb-2 space-x-2">
                  <h2 className="text-xl font-bold text-black flex items-center gap-2">
                    Work Experience
                  </h2>
                  {isAnalized && (
                    <ResumeAnalyzeTooltip
                      content={cvAnalyzeData?.work_experience.analyze_message || ""}
                      type={cvAnalyzeData?.work_experience.status || ""}
                      onClose={() => { }}
                    />
                  )}
                </div>
                <WorkExperience
                  isEditing={isEditing}
                  isDisabled={cvDataInfo?.filename === ""}
                  data={cvDataInfo?.CV_data.work_experience || []}
                  onChange={(newValues: WorkExperienceType[]) => {
                    onChangeCVDataInfo("work_experience", newValues);
                  }}
                  onEdit={handleEditToggle}
                />
              </section>

              {/* Education Section */}
              <section ref={educationRef}>
                <div className="flex items-center mb-2 space-x-2">
                  <h2 className="text-xl font-bold text-black flex items-center gap-2">
                    Education
                  </h2>
                  {isAnalized && (
                    <ResumeAnalyzeTooltip
                      content={cvAnalyzeData?.education.analyze_message || ""}
                      type={cvAnalyzeData?.education.status || ""}
                      onClose={() => { }}
                    />
                  )}
                </div>
                <Education
                  isEditing={isEditing}
                  isDisabled={cvDataInfo?.filename === ""}
                  data={cvDataInfo?.CV_data.education || []}
                  onChange={(newValues: EducationType[]) => {
                    onChangeCVDataInfo("education", newValues);
                  }}
                  onEdit={handleEditToggle}
                />
              </section>

              {/* Projects Section */}
              <section ref={projectsRef}>
                <div className="flex items-center mb-2 space-x-2">
                  <h2 className="text-xl font-bold text-black flex items-center gap-2">
                    Projects
                  </h2>
                  {isAnalized && (
                    <ResumeAnalyzeTooltip
                      content={cvAnalyzeData?.projects.analyze_message || ""}
                      type={cvAnalyzeData?.projects.status || ""}
                      onClose={() => { }}
                    />
                  )}
                </div>
                <Projects
                  isEditing={isEditing}
                  isDisabled={cvDataInfo?.filename === ""}
                  data={cvDataInfo?.CV_data.projects || []}
                  onChange={(newValues: Project[]) => {
                    onChangeCVDataInfo("projects", newValues);
                  }}
                  onEdit={handleEditToggle}
                />
              </section>

              {/* Awards Section */}
              <section ref={awardsRef}>
                <div className="flex items-center mb-2 space-x-2">
                  <h2 className="text-xl font-bold text-black flex items-center gap-2">
                    Awards
                  </h2>
                  {isAnalized && (
                    <ResumeAnalyzeTooltip
                      content={cvAnalyzeData?.awards.analyze_message || ""}
                      type={cvAnalyzeData?.awards.status || ""}
                      onClose={() => { }}
                    />
                  )}
                </div>
                <Awards
                  isEditing={isEditing}
                  isDisabled={cvDataInfo?.filename === ""}
                  data={cvDataInfo?.CV_data.awards || []}
                  onChange={(newValues: AwardType[]) => {
                    onChangeCVDataInfo("awards", newValues);
                  }}
                  onEdit={handleEditToggle}
                />
              </section>

              {/* Certifications Section */}
              <section ref={certificationsRef}>
                <div className="flex items-center mb-2 space-x-2">
                  <h2 className="text-xl font-bold text-black flex items-center gap-2">
                    Certifications
                  </h2>
                  {isAnalized && (
                    <ResumeAnalyzeTooltip
                      content={cvAnalyzeData?.certifications.analyze_message || ""}
                      type={cvAnalyzeData?.certifications.status || ""}
                      onClose={() => { }}
                    />
                  )}
                </div>
                <Certifications
                  isEditing={isEditing}
                  isDisabled={cvDataInfo?.filename === ""}
                  data={cvDataInfo?.CV_data.certifications || []}
                  onChange={(newValues: Certificate[]) => {
                    onChangeCVDataInfo("certifications", newValues);
                  }}
                  onEdit={handleEditToggle}
                />
              </section>
            </div>
          </>
        )
        }

        {/* Edit Mode Controls - Only shown when editing */}
        {
          isEditing && (
            <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-t hidden sm:flex border-gray-300 justify-end gap-4">
              <Button
                variant="outline"
                disabled={isLoadingUpdateCV || isDisabledButton}
                className="text-black border-gray-300 hover:bg-gray-100"
                onClick={handleEditToggle}
              >
                Cancel
              </Button>
              <Button
                disabled={isLoadingUpdateCV || isDisabledButton}
                className="bg-blue-600 hover:bg-blue-800 text-white"
                onClick={onSaveCVData}
              >
                Save Changes
              </Button>
            </div>
          )
        }
      </div >
      {/* Work Preferences Modal */}
      <WorkPreferencesModal
        isOpen={isWorkPreferencesModalOpen}
        onClose={handleWorkPreferencesClose}
        onSave={() => { }}
      />

      <LoadingOverlay
        status={isLoadingUpdateCV ? "loading" : error ? "error" : !isCVUpdated ? "idle" : "success"}
        message={{
          loading: "Updating your profile...",
          success: "Your profile has been updated.",
          error: "Failed to update your profile.",
        }}
        onAutoHide={onHideLoadingOverlay}
      />
    </AuthLayout >
  );
}
